/* eslint-disable no-unused-vars */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import tenant from '@/services/TenantService';

const steps = [
  'Zertifikat erstellen',
  'DNS Einträge konfigurieren',
  'Distribution konfigurieren',
];

export default function StandaloneDialog({
  open,
  onClose,
  deployment,
  onFinish,
  update,
  domain,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const [dnsValidation, setDnsValidation] = useState(null);
  const [distributionDomain, setDistributionDomain] = useState(null);
  const [domainName, setDomainName] = useState(null);
  const [certificateArn, setCertificateArn] = useState(null);

  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(async () => {
      try {
        let res;
        if (update) {
          res = await tenant.get(
            `/certificate/status/update/${deployment._id}`,
          );
        } else {
          res = await tenant.get(
            `/certificate/status/${deployment.insertedId || deployment._id}`,
          );
        }
        const data = res.data;

        if (
          data?.certificateStatus === 'ISSUED' &&
          data?.cloudfrontDomainName
        ) {
          clearInterval(interval);
          setIsPolling(false);
          setDistributionDomain(data?.cloudfrontDomainName);
          setDomainName(data?.domainName);
          toast.success('Zertifikat erfolgreich ausgestellt!');
          setActiveStep(2);
        }
      } catch (err) {
        toast.error(
          <ToastMessage
            title="Fehler"
            subtitle="Zertifikatstatus konnte nicht abgerufen werden"
          />,
        );
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [isPolling]);

  const handleCreateCertificate = async () => {
    setIsLoading(true);
    try {
      if (update) {
        const response = await tenant.post(
          `/certificate/create-on-update/${deployment._id}`,
          { domain: domain },
        );
        setDnsValidation(response.data.dnsValidation);
        setCertificateArn(response.data.certificateArn);
      } else {
        const response = await tenant.post(
          `/certificate/create/${deployment.insertedId || deployment._id}`,
        );
        setDnsValidation(response.data.dnsValidation);
        setCertificateArn(response.data.certificateArn);
      }

      setActiveStep(1);
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Zertifikat konnte nicht erstellt werden"
        />,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} in die Zwischenablage kopiert`);
  };

  const handleCleanup = async () => {
    if (activeStep === 2) {
      onClose();
      setIsPolling(false);
      setActiveStep(0);
      setDnsValidation(null);
      setDistributionDomain(null);
      setDomainName(null);
      setCertificateArn(null);
      onFinish(deployment);
    } else if (certificateArn && activeStep < 2) {
      try {
        if (update) {
          await tenant.delete(`/certificate/${certificateArn}`, {
            data: { deploymentId: deployment._id },
          });
        } else {
          await tenant.delete(
            `/certificate/deployment/${deployment.insertedId || deployment._id}`,
          );
        }
      } catch (error) {
        toast.error(
          <ToastMessage
            title="Fehler"
            subtitle="Zertifikat konnte nicht gelöscht werden"
          />,
        );
      } finally {
        setCertificateArn(null);
      }
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography gutterBottom>
              Klicken Sie auf den Button um ein SSL-Zertifikat für Ihre Domain
              zu erstellen.
            </Typography>
            <Button
              variant="contained"
              onClick={handleCreateCertificate}
              disabled={isLoading}
              sx={{ mt: 2 }}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                'Zertifikat erstellen'
              )}
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography gutterBottom>
              Fügen Sie bitte die folgenden DNS-Einträge in Ihrem DNS-Anbieter
              hinzu:
            </Typography>
            <Box my={2}>
              <Typography variant="subtitle2" gutterBottom>
                Typ
              </Typography>
              <TextField
                fullWidth
                value={dnsValidation?.type}
                InputProps={{ readOnly: true }}
              />

              <Typography variant="subtitle2" gutterBottom mt={2}>
                Name
              </Typography>
              <Box display="flex">
                <TextField
                  fullWidth
                  value={dnsValidation?.name}
                  InputProps={{ readOnly: true }}
                />
                <IconButton
                  onClick={() =>
                    handleCopyToClipboard(dnsValidation?.name, 'Name')
                  }
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>

              <Typography variant="subtitle2" gutterBottom mt={2}>
                Wert
              </Typography>
              <Box display="flex">
                <TextField
                  fullWidth
                  value={dnsValidation?.value}
                  InputProps={{ readOnly: true }}
                />
                <IconButton
                  onClick={() =>
                    handleCopyToClipboard(dnsValidation?.value, 'Wert')
                  }
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 2, mb: 3 }}
            >
              Zusätzlich sollten Sie eine E-Mail von uns erhalten haben, welche
              ebenfalls eine Anleitung zum Verifizieren der Domain enthält.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                onClick={() => setIsPolling(true)}
                disabled={isPolling}
              >
                DNS-Einträge wurden hinzugefügt
              </Button>
              {isPolling && <CircularProgress size={24} />}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography gutterBottom>
              Fügen Sie bitte den folgenden CNAME-Eintrag in Ihrem DNS-Anbieter
              hinzu:
            </Typography>
            <Box my={2}>
              <Typography variant="subtitle2" gutterBottom>
                Typ
              </Typography>
              <TextField
                fullWidth
                value="CNAME"
                InputProps={{ readOnly: true }}
              />

              <Typography variant="subtitle2" gutterBottom mt={2}>
                Name
              </Typography>
              <Box display="flex">
                <TextField
                  fullWidth
                  value={domainName || ''}
                  InputProps={{ readOnly: true }}
                />
                <IconButton
                  onClick={() => handleCopyToClipboard(domainName, 'Domain')}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>

              <Typography variant="subtitle2" gutterBottom mt={2}>
                Wert
              </Typography>
              <Box display="flex">
                <TextField
                  fullWidth
                  value={distributionDomain || ''}
                  InputProps={{ readOnly: true }}
                />
                <IconButton
                  onClick={() =>
                    handleCopyToClipboard(
                      distributionDomain,
                      'Distribution Domain',
                    )
                  }
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, mb: 3 }}
            >
              Dieser DNS-Eintrag leitet Ihre Domain an die CloudFront
              Distribution weiter. Die Änderung kann bis zu 24 Stunden dauern.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleCleanup();
        onClose();
        setIsPolling(false);
        setActiveStep(0);
        setDnsValidation(null);
        setDistributionDomain(null);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>SSL-Zertifikat Konfiguration</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            handleCleanup();
            onClose();
            setActiveStep(0);
            setIsPolling(false);
            setDistributionDomain(null);
            setDnsValidation(null);
          }}
        >
          Abbrechen
        </Button>
        {activeStep === 2 && (
          <Button
            onClick={() => {
              onClose();
              onFinish(deployment);
            }}
            variant="contained"
          >
            Fertigstellen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
